import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Global error boundary so that a single component crash doesn't render the whole app black.
 * Shows a friendly recovery screen with options to reload or go to dashboard.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary]', error, errorInfo);
        // Best-effort: report to backend if possible
        try {
            const token = localStorage.getItem('token');
            const apiBase = process.env.REACT_APP_BACKEND_URL;
            if (token && apiBase) {
                fetch(`${apiBase}/api/system/client-error`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        message: error?.message || String(error),
                        stack: error?.stack,
                        componentStack: errorInfo?.componentStack,
                        url: window.location.href,
                        ua: navigator.userAgent,
                    }),
                }).catch(() => {});
            }
        } catch (e) { /* swallow */ }
    }

    handleReload = () => {
        try {
            // Clear any "broken" cached data without logging out
            sessionStorage.clear();
        } catch { /* ignore */ }
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground" data-testid="error-boundary">
                <div className="max-w-md w-full rounded-2xl border border-border bg-card p-6 shadow-xl space-y-4">
                    <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-xl bg-destructive/10 text-destructive">
                            <AlertTriangle className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-semibold">Da ist etwas schiefgegangen</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Die Seite konnte nicht geladen werden. Versuche es noch einmal — falls es weiter passiert, schick uns kurz Bescheid.
                            </p>
                        </div>
                    </div>

                    {this.state.error?.message && (
                        <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">
                                Technische Details
                            </summary>
                            <pre className="mt-2 p-2 rounded-lg bg-muted/50 text-muted-foreground overflow-x-auto whitespace-pre-wrap">
                                {this.state.error.message}
                            </pre>
                        </details>
                    )}

                    <div className="flex gap-2">
                        <button
                            onClick={this.handleReload}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
                            data-testid="error-reload-btn"
                        >
                            <RefreshCw className="w-4 h-4" /> Seite neu laden
                        </button>
                        <button
                            onClick={this.handleGoHome}
                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl border border-border hover:bg-muted text-sm font-medium transition-colors"
                            data-testid="error-home-btn"
                        >
                            <Home className="w-4 h-4" /> Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
